import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { LUXURY_ESCAPES } from '~/consts/brands';

import UsersService from '~/services/UsersService';

export type RegisterCountryOption = { value: string; label: string };

export function parseRegisterCountryOptions(schema: unknown): RegisterCountryOption[] {
  const countryField = (schema as { post?: { body?: { properties?: { country_code?: unknown } } } })?.post?.body
    ?.properties?.country_code as { enum?: string[]; enumNames?: string[] } | undefined;

  if (!countryField?.enum?.length) {
    return [];
  }

  return countryField.enum.map((value, index) => ({
    value,
    label: countryField.enumNames?.[index] ?? value,
  }));
}

interface RegisterCountryValidationState {
  isLoading: boolean;
  isError: boolean;
}

export function getRegisterCountryCodeError(
  countryCode: string,
  options: RegisterCountryOption[],
  { isLoading, isError }: RegisterCountryValidationState,
): string | null {
  if (isLoading) {
    return 'Country options are still loading.';
  }
  if (isError) {
    return 'Could not load country options. Try refreshing the page.';
  }
  if (options.length === 0) {
    return 'No country options are available.';
  }

  const trimmed = countryCode.trim();
  if (!trimmed) {
    return 'Country is required.';
  }
  if (!options.some((option) => option.value === trimmed)) {
    return 'Select a valid country from the list.';
  }

  return null;
}

export function isRegisterCountrySelectionReady(
  countryCode: string,
  options: RegisterCountryOption[],
  validationState: RegisterCountryValidationState,
): boolean {
  return getRegisterCountryCodeError(countryCode, options, validationState) === null;
}

/**
 * Country options for Infinite new-account registration, derived from the same
 * `/register` schema used by the admin create-user flow.
 */
export function useInfiniteRegisterCountryOptions() {
  const query = useQuery({
    queryKey: ['register-schema', LUXURY_ESCAPES, 'country_code'],
    queryFn: () => UsersService.getRegisterSchema(LUXURY_ESCAPES),
    staleTime: 5 * 60_000,
  });

  const options = useMemo(() => parseRegisterCountryOptions(query.data), [query.data]);

  return {
    options,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
